import { useCallback, useEffect, useRef, useState, RefObject } from 'react';

interface UseFullScreenProps {
  isFullscreen?: boolean;
  /** 외부 ref를 사용할 경우 전달. 없으면 내부 ref 사용 */
  ref?: RefObject<HTMLDivElement | null>;
  /** 'F' 키로 전체화면 토글 활성화 (기본 true). false면 호출자가 직접 키 핸들링 */
  enableKeyboard?: boolean;
}

/**
 * 전체 화면 상태를 관리하는 훅
 */
function useFullScreen({
  isFullscreen: externalIsFullscreen = true,
  ref: externalRef,
  enableKeyboard = true,
}: UseFullScreenProps = {}) {
  const internalRef = useRef<HTMLDivElement>(null);
  const fullscreenRef = externalRef ?? internalRef;

  const [isFullscreen, setIsFullscreen] = useState(false);

  const enterFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      fullscreenRef.current?.requestFullscreen().catch((err) => {
        console.error(
          `Error attempting to enable full-screen mode: ${err.message} (${err.name})`,
        );
      });
    }
  }, [fullscreenRef]);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      enterFullscreen();
    } else {
      exitFullscreen();
    }
  }, [enterFullscreen, exitFullscreen]);

  const handleFullscreenChange = useCallback(() => {
    setIsFullscreen(!!document.fullscreenElement);
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!enableKeyboard || !externalIsFullscreen) return;

      const active = document.activeElement;
      if (active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA') return;

      if (e.code === 'KeyF') {
        toggleFullscreen();
      }
    };

    if (enableKeyboard) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (enableKeyboard) {
        window.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [
    handleFullscreenChange,
    toggleFullscreen,
    enableKeyboard,
    externalIsFullscreen,
  ]);

  return {
    fullscreenRef,
    isFullscreen,
    toggleFullscreen,
  };
}

export default useFullScreen;
